import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import type { components } from '@/types/api/v1'

const REFERENCE_DATA_PATH = '/integration-management/reference-data'

export type Solution = components['schemas']['AnySolution-Output']
export type SolutionType = Solution['type']
type SolutionOf<T extends SolutionType> = Extract<Solution, { type: T }>

/** Values reference-data accepts for `object` to pick its grid, from
 * ObjectContext. A mapping type outside this set just lands on the default. */
const OBJECT_PARAMS = new Set(['terminal', 'product', 'site', 'counterparty'])

function objectParam(mappingType: string | null | undefined) {
  return mappingType && OBJECT_PARAMS.has(mappingType) ? mappingType : undefined
}

function referenceDataUrl(params: Record<string, string | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value)
  }
  return `${REFERENCE_DATA_PATH}?${search.toString()}`
}

/** Reference data, filtered to the solution's own mappings. */
function mappingKeysUrl(solution: {
  mapping_keys: { source_id?: string | null }[]
  mapping_type?: string | null
}): string | null {
  const sourceIds = solution.mapping_keys
    .map((key) => key.source_id)
    .filter((id): id is string => !!id)
  if (!sourceIds.length) return null
  return referenceDataUrl({
    object: objectParam(solution.mapping_type),
    source_ids: sourceIds.join(','),
  })
}

/**
 * Solution types the UI can act on today. A type listed here renders as a link
 * to wherever the user resolves it; every other type renders as plain
 * instructive text. Add a type once its destination exists.
 *
 * Deliberately unlisted, because there is nothing to click:
 *  - confirm_correct_delivery: asks the user to verify a delivery out of band
 *  - use_tank_ids: tells the partner to change what they send us
 */
const SOLUTION_HANDLERS: {
  [T in SolutionType]?: (solution: SolutionOf<T>) => string | null
} = {
  create_mapping: (s) =>
    referenceDataUrl({
      object: objectParam(s.mapping_type),
      action: 'create_mapping',
      prefill_type: s.mapping_type ?? undefined,
      prefill_source_id: s.mapping_key.source_id ?? undefined,
      prefill_source_name: s.mapping_key.source_name ?? undefined,
    }),

  link_to_master: (s) => {
    const sourceId = s.mapping_key?.source_id
    if (!sourceId && !s.goid) return null
    return referenceDataUrl({
      object: objectParam(s.mapping_type),
      source_ids: sourceId ?? undefined,
      go_ids: s.goid ?? undefined,
    })
  },

  unlink_mappings: mappingKeysUrl,

  // Lands on the suppliers that were considered, so the user can fill in the
  // Linked Terminals/Products/Price Types the rule had nothing to go on.
  set_supplier_filters: mappingKeysUrl,
}

/** A tenant owns a solution when its id is listed on that solution. */
export function ownsSolution(
  solution: Solution,
  tenantId: string | null | undefined,
): boolean {
  return !!tenantId && solution.tenant_ids.includes(tenantId)
}

/**
 * A solution naming no tenant has nobody on either side of the flow to act on
 * it, so it's internal-only -- shown to Gravitate users and hidden from
 * everyone else.
 */
export function isUnownedSolution(solution: Solution): boolean {
  return solution.tenant_ids.length === 0
}

/** Tenant id -> display name for both sides of the log's flow. */
export function tenantNamesFromRouting(
  routing: CrossroadsLog['routing'] | undefined,
): Map<string, string> {
  const namesById = new Map<string, string>()
  if (routing?.origin_tenant_id) {
    namesById.set(routing.origin_tenant_id, routing.origin_tenant_name)
  }
  if (routing?.destination_tenant_id) {
    namesById.set(
      routing.destination_tenant_id,
      routing.destination_tenant_name,
    )
  }
  return namesById
}

/**
 * Who a solution belongs to, for the Owner column. The viewer's own tenant is
 * addressed as "You"; anyone else is named from the routing. An id matching
 * neither side of the flow falls back to a generic label rather than exposing
 * a raw ObjectId.
 */
export function solutionOwnerLabel(
  solution: Solution,
  tenantId: string | null | undefined,
  namesById: Map<string, string>,
): string {
  // No tenant to name -- "Partner" below would be a lie.
  if (isUnownedSolution(solution)) return '-'
  if (ownsSolution(solution, tenantId)) return 'You'
  const names = solution.tenant_ids
    .map((id) => namesById.get(id))
    .filter((name): name is string => !!name)
  return names.length ? [...new Set(names)].join(', ') : 'Partner'
}

/** The destination for a solution, or null when it is informational only. */
export function buildSolutionUrl(solution: Solution): string | null {
  const handler = SOLUTION_HANDLERS[solution.type]
  if (!handler) return null
  // The lookup key is the same discriminator we're passing in, but TS can't
  // correlate the two across the mapped type.
  return (handler as (s: Solution) => string | null)(solution)
}
